/**
 * Упоминания ассетов сцены ↔ читаемый текст — для мест, где промпт уходит к языковой модели не на генерацию,
 * а на переписывание (улучшатель промпта). Модель видит «@Анна», а не @{uuid}@, и сохраняет имя; ответ
 * возвращается чипами. Ассет без имени (не подгружен) — упоминание убираем, uuid модели не показываем.
 */
import { assetIdOf, assetIdsInPrompt, ASSET_TOKEN_SRC, assetToken } from '../prompt-vars/phrases'

/** Имена для улучшателя — только ассетов, упомянутых в этом тексте, и каждое своё: у двух ассетов с одним именем
 *  («Анна» своя и «Анна» из библиотеки) второй получает номер, иначе на обратном пути оба стали бы одним. */
export function mentionAliases(text: string, names: Record<string, string>): Record<string, string> {
	const out: Record<string, string> = {}
	const used = new Set<string>()
	for (const id of assetIdsInPrompt(text)) {
		const name = names[id]
		if (!name) continue
		let alias = name
		for (let n = 2; used.has(alias.toLowerCase()); n++) alias = `${name} ${n}`
		used.add(alias.toLowerCase())
		out[id] = alias
	}
	return out
}

export function assetTokensToNames(text: string, names: Record<string, string>): string {
	return (text || '').replace(new RegExp(ASSET_TOKEN_SRC, 'g'), (tok) => {
		const id = assetIdOf(tok)
		const name = id ? names[id] : undefined
		return name ? `@${name}` : ''
	})
}

const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export function assetNamesToTokens(text: string, names: Record<string, string>): string {
	let out = text || ''
	// Длинные имена раньше коротких: «@Анна Мария» не должен стать «@Анна» + « Мария».
	for (const [id, name] of Object.entries(names).sort((a, b) => b[1].length - a[1].length)) {
		if (!name) continue
		out = out.replace(new RegExp(`@${esc(name)}(?![\\p{L}\\p{N}])`, 'gu'), assetToken(id))
	}
	return out
}

/** Промпт для показа человеку (карточка результата, «Копировать промпт», история вкладки): упоминание — просто
 *  имя, как его запишет сервер; имя неизвестно — упоминание убираем (uuid человеку ни о чём не говорит). */
export function assetTokensToPlain(text: string, names: Record<string, string>): string {
	if (!text || !text.includes('@{')) return text || ''
	const out = text.replace(new RegExp(ASSET_TOKEN_SRC, 'g'), (tok) => {
		const id = assetIdOf(tok)
		return (id && names[id]) || ''
	})
	return out.replace(/[ \t]{2,}/g, ' ').replace(/[ \t]+([,.;:!?])/g, '$1').trim()
}

/** Следующая остановка «печатающей» анимации после позиции i: упоминание ассета печатается целиком за один шаг —
 *  иначе в поле по буквам бежит @{uuid}@ и только в конце становится чипом. */
export function nextTypeStop(text: string, i: number): number {
	if (text[i] === '@' && text[i + 1] === '{') {
		const m = new RegExp(`^${ASSET_TOKEN_SRC}`).exec(text.slice(i, i + 48))
		if (m) return i + m[0].length
	}
	return Math.min(text.length, i + 1)
}
